// مشترک صفحات پیام‌رسانی (AP-MSG-01، AP-MSG-02): کلیدهای Query، شناسه صفحات، برچسب وضعیت و تبدیل زمان محلی شعبه به UTC.
import type { MessagePurpose } from '@phoenix/api-client';
import { useTranslation } from '@phoenix/i18n';
import { StatusChip, type StatusTone } from '@phoenix/ui';

export const SCR_STAFF_950 = 'SCR-STAFF-950';
export const SCR_STAFF_951 = 'SCR-STAFF-951';
export const SCR_STAFF_952 = 'SCR-STAFF-952';
export const SCR_STAFF_953 = 'SCR-STAFF-953';
export const SCR_STAFF_954 = 'SCR-STAFF-954';
export const SCR_STAFF_955 = 'SCR-STAFF-955';
export const COMP_NOTIFICATION_CENTER = 'COMP-NOTIFICATION-CENTER';
export const COMP_PATIENT_MESSAGES = 'COMP-PATIENT-MESSAGES';

export const msgKeys = {
  all: ['messaging'] as const,
  settings: (tenantId?: string) => ['messaging', 'settings', tenantId] as const,
  templates: (tenantId?: string, language = 'fa') => ['messaging', 'templates', tenantId, language] as const,
  template: (tenantId: string | undefined, code: string) =>
    ['messaging', 'templates', tenantId, code] as const,
  rules: (tenantId?: string) => ['messaging', 'rules', tenantId] as const,
  log: (tenantId: string | undefined, status: string, purpose: string) =>
    ['messaging', 'log', tenantId, status, purpose] as const,
  message: (tenantId: string | undefined, id: string) => ['messaging', 'message', tenantId, id] as const,
  patient: (tenantId: string | undefined, patientId: string) =>
    ['messaging', 'patient', tenantId, patientId] as const,
  inbound: (tenantId: string | undefined, status: string) =>
    ['messaging', 'inbound', tenantId, status] as const,
  bulk: (tenantId?: string) => ['messaging', 'bulk', tenantId] as const,
  notifications: (tenantId?: string) => ['messaging', 'notifications', tenantId] as const,
  summary: (tenantId?: string) => ['messaging', 'notifications', tenantId, 'summary'] as const,
  preferences: (tenantId?: string) => ['messaging', 'preferences', tenantId] as const,
  usage: (tenantId: string | undefined, from: string, to: string, channel: string, purpose: string) =>
    ['messaging', 'usage', tenantId, from, to, channel, purpose] as const,
};

/** رنگ وضعیت پیام (AC-MSG-070.1). */
export function messageTone(status: string): StatusTone {
  switch (status) {
    case 'Delivered':
      return 'done';
    case 'Sent':
      return 'confirmed';
    case 'Queued':
      return 'booked';
    case 'Deferred':
      return 'pending';
    case 'Failed':
    case 'Undeliverable':
      return 'error';
    default:
      return 'cancelled';
  }
}

export function MessageStatusChip({ status, testId }: { status: string; testId?: string }) {
  return (
    <span data-testid={testId} data-status={status}>
      <StatusChip tone={messageTone(status)} labelKey={`msg:status.${status}`} />
    </span>
  );
}

/** برچسب هدف پیام. */
export function usePurposeLabel() {
  const { t } = useTranslation('msg');
  return (purpose: MessagePurpose | string) => t(`msg:purpose.${purpose}`, { defaultValue: purpose });
}

/** برچسب قالب (کد ← عنوان؛ کد ناشناخته همان کد). */
export function useTemplateLabel() {
  const { t } = useTranslation('msg');
  return (code: string) => t(`msg:template_names.${code.replaceAll('.', '_')}`, { defaultValue: code });
}

/** برچسب علت عدم ارسال. */
export function useReasonLabel() {
  const { t } = useTranslation('msg');
  return (reason: string | null | undefined) =>
    reason ? t(`msg:reason.${reason}`, { defaultValue: reason }) : '';
}

function zoneOffsetMinutes(instant: number, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(instant));
  const get = (type: string) => Number(parts.find((part) => part.type === type)?.value ?? '0');
  const asUtc = Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    get('hour'),
    get('minute'),
    get('second'),
  );
  return Math.round((asUtc - instant) / 60_000);
}

/** تاریخ کسب‌وکاری (`yyyy-MM-dd`) و ساعت محلی (`HH:mm`) در منطقه زمانی ← لحظه UTC (ISO). */
export function localToUtc(businessDate: string, time: string, timeZone: string): string {
  const [year, month, day] = businessDate.split('-').map(Number) as [number, number, number];
  const [hour, minute] = time.split(':').map(Number) as [number, number];
  const naive = Date.UTC(year, month - 1, day, hour, minute);
  const first = naive - zoneOffsetMinutes(naive, timeZone) * 60_000;
  const corrected = naive - zoneOffsetMinutes(first, timeZone) * 60_000;
  return new Date(corrected).toISOString();
}
